import React, { useCallback } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

import formatStrategyBenchmarkRateOfReturn from "../../../../../lib/StrategyBenchmarkChart/formatStrategyBenchmarkRateOfReturn";
import { STRATEGY_BENCHMARK_CHART_COLORS } from "../../../../../lib/StrategyBenchmarkChart/strategyBenchmarkChartColors";
import StrategyBenchmarkChartOptionSelect from "./StrategyBenchmarkChartOptionSelect";

const COMPARISON_COLORS = {
  wig20: STRATEGY_BENCHMARK_CHART_COLORS.wig20,
  mwig40: STRATEGY_BENCHMARK_CHART_COLORS.mwig40,
  swig80: STRATEGY_BENCHMARK_CHART_COLORS.swig80
};

const SUBLABEL_CLASSES = "text-xs.3 leading-130 text-black-8 font-normal";

const StrategyBenchmarkChartRate = ({ rate, locale }) => (
  <span
    className={classNames(
      "text-sm font-semibold leading-130 whitespace-nowrap shrink-0",
      rate >= 0 ? "text-green-3" : "text-red-3"
    )}
  >
    {formatStrategyBenchmarkRateOfReturn(rate, locale)}
  </span>
);

StrategyBenchmarkChartRate.propTypes = {
  rate: PropTypes.number.isRequired,
  locale: PropTypes.string.isRequired
};

const ComparisonPickerRadio = ({ color, isSelected }) => (
  <span
    className="w-3.5 h-3.5 rounded-full border-2 border-solid shrink-0 flex items-center justify-center"
    style={{ borderColor: color, color }}
    aria-hidden="true"
  >
    {isSelected ? (
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{ backgroundColor: color }}
      />
    ) : null}
  </span>
);

ComparisonPickerRadio.propTypes = {
  color: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired
};

const StrategyBenchmarkChartComparisonPicker = ({
  locale,
  translate,
  strategyLabelKey,
  comparisonType,
  comparisonOptions,
  rates,
  onComparisonChange
}) => {
  const compareLabel = translate(
    "strategy_benchmark_chart_compare_with_count",
    {
      count: comparisonOptions.length
    }
  );
  const selectedOption =
    comparisonOptions.find(option => option.value === comparisonType) ||
    comparisonOptions[0];
  const selectedLabel = selectedOption?.label || comparisonType;
  const selectedColor =
    COMPARISON_COLORS[comparisonType] ||
    STRATEGY_BENCHMARK_CHART_COLORS.wig20;
  const triggerLabel = `${compareLabel} ${selectedLabel}`;
  const rateOfReturnLabel = translate(
    "strategy_benchmark_chart_rate_of_return"
  );

  const handleSelect = useCallback(
    (type, close) => {
      onComparisonChange(type);
      close();
    },
    [onComparisonChange]
  );

  return (
    <StrategyBenchmarkChartOptionSelect
      triggerLabel={triggerLabel}
      ariaLabel={triggerLabel}
      triggerContent={
        <span className="flex flex-col min-w-0">
          <span className="strategy-benchmark-chart__select-label">
            {compareLabel}
          </span>
          <span
            className="text-sm font-bold leading-100"
            style={{ color: selectedColor }}
          >
            {selectedLabel}
          </span>
        </span>
      }
    >
      {({ listboxId, close, listboxClassName, triggerLabel: label }) => (
        <div
          id={listboxId}
          role="listbox"
          className={listboxClassName}
          aria-label={label}
        >
          <div className="strategy-benchmark-chart__select-option px-3.5 py-3 border-bottom border-bottom-gray-2">
            <div className="strategy-benchmark-chart__select-option-label flex-col gap-0.5 items-start">
              <span className="text-sm font-bold leading-100 text-link truncate w-full">
                {translate(strategyLabelKey)}
              </span>
              <span className={SUBLABEL_CLASSES}>{rateOfReturnLabel}</span>
            </div>
            <span className="strategy-benchmark-chart__select-option-rate">
              <StrategyBenchmarkChartRate
                rate={rates.strategy}
                locale={locale}
              />
            </span>
          </div>

          {comparisonOptions.map((option, index) => {
            const type = option.value;
            const color =
              COMPARISON_COLORS[type] || STRATEGY_BENCHMARK_CHART_COLORS.wig20;
            const isSelected = comparisonType === type;

            return (
              <button
                key={type}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={classNames(
                  "strategy-benchmark-chart__select-option px-3.5 py-3 border-none bg-transparent cursor-pointer text-left outline-none hover:bg-gray-10 focus-visible:bg-gray-10 focus-visible:outline-none",
                  index < comparisonOptions.length - 1 &&
                    "border-bottom border-bottom-gray-2"
                )}
                onClick={() => handleSelect(type, close)}
              >
                <span className="strategy-benchmark-chart__select-option-label">
                  <ComparisonPickerRadio
                    color={color}
                    isSelected={isSelected}
                  />
                  <span className="flex flex-col gap-0.5 min-w-0 overflow-hidden">
                    <span
                      className="text-sm font-semibold leading-100 truncate"
                      style={{ color }}
                    >
                      {option.label}
                    </span>
                    <span className={SUBLABEL_CLASSES}>
                      {rateOfReturnLabel}
                    </span>
                  </span>
                </span>
                <span className="strategy-benchmark-chart__select-option-rate">
                  <StrategyBenchmarkChartRate
                    rate={rates[type]}
                    locale={locale}
                  />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </StrategyBenchmarkChartOptionSelect>
  );
};

StrategyBenchmarkChartComparisonPicker.propTypes = {
  locale: PropTypes.string.isRequired,
  translate: PropTypes.func.isRequired,
  strategyLabelKey: PropTypes.string.isRequired,
  comparisonType: PropTypes.string.isRequired,
  comparisonOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  rates: PropTypes.shape({
    strategy: PropTypes.number.isRequired
  }).isRequired,
  onComparisonChange: PropTypes.func.isRequired
};

export default StrategyBenchmarkChartComparisonPicker;
